import React, {
	type ReactElement,
	useContext,
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from 'react';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';
import { type ExternalUser } from '@atlaskit/user-picker';
import {
	type WithAnalyticsEventsProps,
	useAnalyticsEvents,
	type CreateUIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import type Fuse from 'fuse.js';
import type { SlackService } from '@atlassian/integrations';
import { slackServiceFactory } from '@atlassian/integrations';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type OrgSettings,
	fetchExistingIntegrations,
	fetchExistingIntegrations_deprecated,
	search as thirdPartyIntegrationSearch,
} from '@atlassian/integrations/third-party';

import {
	type SlackWorkspace,
	type GenericShowFlag,
	ScopeState,
	type IntegrationMetadata,
	type IntegrationsScopeState,
	Integration,
} from '../../../types';
import { isSLOFailure } from '../../../clients/helpers';
import {
	startUfoExperience,
	succeedUfoExperience,
	failUfoExperience,
	thirdPartyResultsLoaded,
	invitePeopleRendered,
	invitePeopleRenderedTimings,
} from '../../../analytics/ufoExperiences';
import { isAcceptableFailure, resolveError, slackToExternalUser } from '../../../utils';
import { useOrgControlSettings } from '../../../experiments/orgControlSettings/useOrgControlSettings';
import { useAnalytics } from '../../../analytics';
import { useThirdPartyIntegrationsFlags } from '../../Flags/ThirdPartyIntegrations';
import { useInvitePeopleContext } from '../../InvitePeople';
import {
	MICROSOFT_SERVICE,
	GOOGLE_SERVICE,
	MAX_USERS,
	PLATFORM_SLACK_SERVICE,
	FETCH_INTEGRATIONS_FAILURE_MESSAGE,
	SLACK_SOURCE,
	type ThirdPartySource,
} from '../constants';
import { mergeExternalUsers, manageSlackWorkspaces, fuseFactory } from '../utils';
import {
	disconnectSlackWorkSpace,
	getConnectedSlackWorkSpace,
	setConnectedSlackWorkSpace,
} from '../localStorageUtils';
import { getATLContextUrl } from '@atlassian/atl-context';
import { ThirdPartyContextAnalytics } from './ThirdPartyContextAnalytics';
import { useOnChangeSelectedSlackWorkspace } from './useOnChangeSelectedSlackWorkspace';
import { useOnConnectProvider } from './useOnConnectProvider';
import { useUsersByThirdPartyId } from './useUsersByThirdPartyId';
import { canInvitePeople } from '../../ProductSelect';

export type ThirdPartyState = {
	isLoading: boolean;
	isOk?: boolean;
	slackWorkspaces: SlackWorkspace[];
	selectedSlackWorkspace: SlackWorkspace | null;
	isSlackConnectDialogOpen: boolean;
	integrations: Set<Integration>;
	enabledIntegrations: Set<Integration>;
	closeSlackConnectDialog: () => void;
	openSlackConnectDialog: () => void;
	onChangeSelectedSlackWorkspace: (selectedSlackWorkspace: SlackWorkspace) => void;
	onDisconnectSlackWorkspace: () => void;
	onThirdPartyIntegrationsChange: () => void;
	queryThirdParty: (searchText: string | undefined) => Promise<ExternalUser[]>;
	/** Returning null = invalid user (eg. bot) */
	getUserByThirdPartyId: (source: ThirdPartySource, id: string) => ExternalUser | null | undefined;
	cacheUserByThirdPartyId: (
		source: ThirdPartySource,
		id: string,
		/** null = invalid user (eg. bot) */
		user: ExternalUser | null,
	) => void;
	onConnectProvider: (args: {
		serviceKey: Integration;
		forceConnect?: boolean;
		fromUpdateConnection?: boolean;
		scopeState?: ScopeState;
		customAnalyticsFn?: (createAnalyticsEvent: CreateUIAnalyticsEvent, serviceKey: string) => void;
	}) => void;
	onDisconnectProvider: (serviceKey: string) => void;
	abortThirdPartyRequests: () => void;
	updateIntegrations: IntegrationsScopeState;
	orgSettings: OrgSettings;
	isConnected: boolean;
	hideThirdPartyConnectLegend?: boolean;
};

const ThirdPartyStateContext = React.createContext<ThirdPartyState | undefined>(undefined);

export interface ThirdPartyContextProps {
	enableThirdParty?: boolean;
	showFlag?: GenericShowFlag<any>;
	onCancelButtonClick?: () => void;
	hideThirdPartyConnectLegend?: boolean;
	children: ReactElement;
	resourceAri: string;
}

export const ThirdPartyContext = ({
	enableThirdParty,
	showFlag,
	children,
	onCancelButtonClick,
	hideThirdPartyConnectLegend,
	resourceAri,
}: ThirdPartyContextProps & WithAnalyticsEventsProps): React.JSX.Element => {
	const { orgId, cloudId, productId, jiraSubProduct } = useInvitePeopleContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analytics = useAnalytics(ThirdPartyContextAnalytics);
	const thirdPartyIntegrationsFlags = useThirdPartyIntegrationsFlags();
	const [isLoading, setIsLoading] = useState(true);
	const [isAfterSlackConnect, setIsAfterSlackConnect] = useState(false);
	const [isOk, setIsOk] = useState<boolean | undefined>(undefined);
	const [isConnected, setIsConnected] = useState(false);
	const [slackService, setSlackService] = useState<SlackService | undefined>(undefined);
	const [slackWorkspaces, setSlackWorkspaces] = useState<SlackWorkspace[]>([]);
	const [selectedSlackWorkspace, setSelectedSlackWorkspace] = useState<SlackWorkspace | null>(null);

	const [integrations, setIntegrations] = useState<Integration[] | null>(null);

	const [isSlackConnectDialogOpen, setIsSlackConnectDialogOpen] = useState(false);

	const [updateThirdPartyIntegrations, setUpdateThirdPartyIntegrations] =
		useState<IntegrationsScopeState>({
			noScope: new Set<Integration>(),
			partialScope: new Set<Integration>(),
		});

	const [integrationMetadata, setIntegrationMetadata] = useState<IntegrationMetadata | undefined>(
		undefined,
	);

	const { isLoading: isOrgSettingsLoading, settings: orgSettings } = useOrgControlSettings(
		orgId || null,
	);

	const [enabledIntegrations, setEnabledIntegrations] = useState<Set<Integration>>(new Set());

	const abortThirdPartyRequests = useCallback(() => {
		if (slackService) {
			slackService.abort();
		}
	}, [slackService]);

	const slackUserSearcher = useRef<Fuse<ExternalUser>>(fuseFactory([]));

	const { getUserByThirdPartyId, cacheUserByThirdPartyId } = useUsersByThirdPartyId();

	useEffect(() => {
		async function onSetWorkspace() {
			if (!cloudId || !productId) {
				return;
			}

			slackUserSearcher.current = fuseFactory([]);

			if (selectedSlackWorkspace && slackService) {
				slackService.abort();

				slackService.streamUsers(selectedSlackWorkspace.id, (slackUsers) => {
					if (slackUsers && slackUsers.ok) {
						for (const slackUser of slackUsers.result) {
							const externalUser = slackToExternalUser(slackUser);
							slackUserSearcher.current.add(externalUser);
							cacheUserByThirdPartyId(SLACK_SOURCE, slackUser.id, externalUser);
						}
					}
				});
			}
		}

		onSetWorkspace();
	}, [selectedSlackWorkspace, cloudId, productId, slackService, cacheUserByThirdPartyId]);

	const connectedIntegrations: Set<Integration> = useMemo(() => {
		if (!integrations) {
			return new Set<Integration>();
		}

		const res = new Set<Integration>([
			// connected integrations aside from slack
			...integrations.filter((integration) => integration !== PLATFORM_SLACK_SERVICE),
			// we only want to set slack as connected if it is connected according
			// to exus and a workspace is selected
			...(integrations.includes(PLATFORM_SLACK_SERVICE) && selectedSlackWorkspace
				? [PLATFORM_SLACK_SERVICE]
				: []),
		]);

		analytics.integrationsFetched({ integrations: [...res] });
		return res;
	}, [analytics, integrations, selectedSlackWorkspace]);

	const initSlack = useCallback(() => {
		if (!cloudId) {
			return [];
		}

		const productSlackService = slackServiceFactory(cloudId, 'platform', createAnalyticsEvent);

		setSlackService(productSlackService);
	}, [cloudId, createAnalyticsEvent]);

	useEffect(() => {
		if (!integrationMetadata) {
			setIsConnected(connectedIntegrations.size > 0);
			return;
		}
		const noScope = new Set<Integration>();
		const partialScope = new Set<Integration>();

		const connectedProductSlackWorkspace = getConnectedSlackWorkSpace();
		if (connectedProductSlackWorkspace) {
			const connectedTeam = integrationMetadata?.[Integration.PLATFORM_SLACK_SERVICE]?.teams?.find(
				(team) => team.id === connectedProductSlackWorkspace,
			);
			if (connectedTeam) {
				if (connectedTeam.scopeState === ScopeState.NO_SCOPE) {
					noScope.add(Integration.PLATFORM_SLACK_SERVICE);
				}
			} else {
				noScope.add(Integration.PLATFORM_SLACK_SERVICE);
			}
		}

		connectedIntegrations.forEach((product) => {
			if (product !== Integration.PLATFORM_SLACK_SERVICE) {
				if (integrationMetadata?.[product]?.scopeState === ScopeState.NO_SCOPE) {
					noScope.add(product);
				}
				if (integrationMetadata?.[product]?.scopeState === ScopeState.PARTIAL_SCOPE) {
					partialScope.add(product);
				}
			}
		});

		setIsConnected(connectedIntegrations.size > noScope.size);
		setUpdateThirdPartyIntegrations({
			noScope,
			partialScope,
		});
	}, [integrationMetadata, connectedIntegrations, selectedSlackWorkspace?.id, productId]);

	const fetchExistingIntegrationsWithMarker = useCallback(async (cloudId: string) => {
		invitePeopleRendered.mark(invitePeopleRenderedTimings.fetchExistingIntegrations.startMark);
		const integrationsResponse = await (fg('puma-550-remove-cloud-id')
			? fetchExistingIntegrations()
			: fetchExistingIntegrations_deprecated(cloudId));
		invitePeopleRendered.mark(invitePeopleRenderedTimings.fetchExistingIntegrations.endMark);
		return integrationsResponse;
	}, []);

	const [autoConnectedWorkspace, setAutoConnectedWorkspace] = useState<SlackWorkspace | null>(null);

	useEffect(() => {
		async function getThirdPartyIntegrations() {
			if (!cloudId || !productId) {
				setIsLoading(false);
				return;
			}

			try {
				if (enableThirdParty === true) {
					setIsLoading(true);

					if (fg('puma-1317-ci-3p-capabilities')) {
						const userCanInvite = await canInvitePeople({
							jiraSubProduct: jiraSubProduct,
							productId: productId,
							cloudId: cloudId ?? '',
							resourceAri,
							onSuccess: analytics.canInvitePeopleSucceeded.bind(analytics),
							onFail: analytics.canInvitePeopleFailed.bind(analytics),
						});

						// short circuit if the user can't actually invite anyone
						if (!userCanInvite) {
							setIsOk(false);
							return;
						}
					}

					initSlack();

					const integrationsResponse = await fetchExistingIntegrationsWithMarker(cloudId);

					if (!integrationsResponse) {
						throw new Error(FETCH_INTEGRATIONS_FAILURE_MESSAGE);
					}

					setIntegrations(integrationsResponse.connectedIntegrations);
					setEnabledIntegrations(new Set(integrationsResponse.enabledIntegrations));
					setIntegrationMetadata(integrationsResponse?.integrationMetadata);

					// slack workspaces from EXUS filtered by valid scope
					const workspaces =
						integrationsResponse?.integrationMetadata?.[
							Integration.PLATFORM_SLACK_SERVICE
						]?.teams?.filter((workspace) => workspace.scopeState !== ScopeState.NO_SCOPE) || [];
					setSlackWorkspaces(workspaces);

					const slackWorkspaceId = getConnectedSlackWorkSpace();

					if (workspaces.length > 0) {
						const slackWorkspace = slackWorkspaceId
							? workspaces.find((slackWorkspace) => slackWorkspace.id === slackWorkspaceId)
							: undefined;

						if (slackWorkspace) {
							setSelectedSlackWorkspace(slackWorkspace);
						} else {
							setSelectedSlackWorkspace(workspaces[0]);
							setConnectedSlackWorkSpace(workspaces[0].id);
							setAutoConnectedWorkspace(workspaces[0]);
							analytics.integrationConnected({
								serviceKey: PLATFORM_SLACK_SERVICE,
								teamId: workspaces[0].id,
								auto: true,
							});
						}
					}

					setIsOk(integrationsResponse.enabledIntegrations.length > 0);
					analytics.sloThirdPartyInitSuccess({
						enabledIntegrations: integrationsResponse.enabledIntegrations,
						connectedIntegrations: integrationsResponse.connectedIntegrations,
						selectedSlackWorkspace: slackWorkspaceId ?? undefined,
					});
				} else {
					setIsOk(false);
				}
			} catch (error) {
				setIsOk(false);

				const { errorMessage, responseCode } = resolveError(error);
				const is4xxError = responseCode && [400, 401, 403].includes(responseCode);

				if (errorMessage === FETCH_INTEGRATIONS_FAILURE_MESSAGE) {
					analytics.fetchExistingIntegrationsFailed({
						ari: cloudId,
						errorMessage,
						responseCode,
					});
				}

				if (is4xxError || isAcceptableFailure(errorMessage, responseCode)) {
					// Only fired in error cases that should not be measured in the SLO
					analytics.thirdPartyInitFailed(String(error));
				} else if (isSLOFailure(error)) {
					analytics.sloThirdPartyInitFailed(String(error));
				}
			} finally {
				setIsLoading(false);
			}
		}
		getThirdPartyIntegrations();
	}, [
		cloudId,
		productId,
		createAnalyticsEvent,
		enableThirdParty,
		initSlack,
		analytics,
		fetchExistingIntegrationsWithMarker,
		jiraSubProduct,
		resourceAri,
	]);

	// Show flag when auto-connect happens
	useEffect(() => {
		if (autoConnectedWorkspace && showFlag && thirdPartyIntegrationsFlags) {
			showFlag(
				thirdPartyIntegrationsFlags.success(PLATFORM_SLACK_SERVICE, autoConnectedWorkspace.name),
			);
			setAutoConnectedWorkspace(null);
		}
	}, [autoConnectedWorkspace, showFlag, thirdPartyIntegrationsFlags]);

	const onChangeSelectedSlackWorkspace = useOnChangeSelectedSlackWorkspace({
		analytics,
		setSelectedSlackWorkspace,
		showFlag,
	});

	const { slackDisconnect } = useThirdPartyIntegrationsFlags();

	const onDisconnectSlackWorkspace = useCallback(() => {
		if (showFlag) {
			showFlag(slackDisconnect());
		}
		setSelectedSlackWorkspace(null);
		disconnectSlackWorkSpace();
	}, [showFlag, slackDisconnect]);

	const onThirdPartyIntegrationsChange = useCallback(async () => {
		if (!cloudId) {
			return;
		}
		try {
			const integrationsResponse = await (fg('puma-550-remove-cloud-id')
				? fetchExistingIntegrations()
				: fetchExistingIntegrations_deprecated(cloudId));
			if (!integrationsResponse) {
				return;
			}
			setIntegrations(integrationsResponse.connectedIntegrations);
			setIntegrationMetadata(integrationsResponse.integrationMetadata);
			// set slack workspaces from EXUS filtered by valid scope
			setSlackWorkspaces(
				integrationsResponse.integrationMetadata?.[
					Integration.PLATFORM_SLACK_SERVICE
				]?.teams?.filter((workspace) => workspace.scopeState !== ScopeState.NO_SCOPE) || [],
			);
		} catch (error) {
			const { errorMessage, responseCode } = resolveError(error);
			analytics.fetchExistingIntegrationsFailed({
				ari: cloudId,
				errorMessage,
				responseCode,
			});
			return;
		}
	}, [cloudId, analytics]);

	const closeSlackConnectDialog = () => setIsSlackConnectDialogOpen(false);
	const openSlackConnectDialog = () => setIsSlackConnectDialogOpen(true);

	const queryThirdParty = useCallback(
		async (searchText: string | undefined): Promise<ExternalUser[]> => {
			if (
				!cloudId ||
				!searchText ||
				searchText.trim().length === 0 ||
				connectedIntegrations.size === 0
			) {
				return [];
			}

			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			const ufoExperienceId = uuid();

			startUfoExperience(thirdPartyResultsLoaded, ufoExperienceId, {
				connectedIntegrations: Array.from(connectedIntegrations).join(', '),
			});

			const isNonSlackIntegrationConnected =
				connectedIntegrations.has(GOOGLE_SERVICE) || connectedIntegrations.has(MICROSOFT_SERVICE);

			// query backend for users on if MS/Google are connected
			let users = isNonSlackIntegrationConnected
				? await thirdPartyIntegrationSearch(searchText, cloudId).catch((_: unknown) => {
						failUfoExperience(thirdPartyResultsLoaded, ufoExperienceId);
						return [];
				  })
				: [];

			if (slackUserSearcher.current) {
				const slackUsers: ExternalUser[] = slackUserSearcher.current
					.search(searchText)
					.map((result) => result.item);
				users = mergeExternalUsers([
					users,
					slackUsers.slice(0, Math.min(slackUsers.length, MAX_USERS)),
				]);
			}

			const fuse = fuseFactory(users);
			const searchResult: ExternalUser[] = fuse.search(searchText).map((result) => result.item);

			succeedUfoExperience(thirdPartyResultsLoaded, ufoExperienceId);
			return searchResult.slice(0, Math.min(searchResult.length, MAX_USERS));
		},
		[connectedIntegrations, cloudId],
	);

	const openSlackSettings = useCallback(() => {
		manageSlackWorkspaces(
			Boolean(selectedSlackWorkspace),
			slackWorkspaces,
			isSlackConnectDialogOpen,
			openSlackConnectDialog,
			onChangeSelectedSlackWorkspace,
			productId,
			onCancelButtonClick,
		);
	}, [
		isSlackConnectDialogOpen,
		onChangeSelectedSlackWorkspace,
		onCancelButtonClick,
		slackWorkspaces,
		selectedSlackWorkspace,
		productId,
	]);

	// We use this useEffect to open the slack settings after some
	// state (e.g. slackWorkspaces) is changed. we have this on it's own
	// useEffect since we want it to run after setState has finished
	useEffect(() => {
		// Do nothing if user did not manually connect
		if (!isAfterSlackConnect) {
			return;
		}

		setIsAfterSlackConnect(false);
		openSlackSettings();
	}, [isAfterSlackConnect, openSlackSettings, slackWorkspaces]);

	const onConnectProvider = useOnConnectProvider({
		analytics,
		integrations,
		onThirdPartyIntegrationsChange,
		openSlackSettings,
		setIsAfterSlackConnect,
		showFlag,
		slackWorkspaces,
	});

	const onDisconnectProvider = useCallback(
		(serviceKey: string) => {
			if (serviceKey === PLATFORM_SLACK_SERVICE) {
				if (slackWorkspaces.length) {
					openSlackConnectDialog();
				} else {
					setSelectedSlackWorkspace(null);
					disconnectSlackWorkSpace();
					onThirdPartyIntegrationsChange().then(() => showFlag && showFlag(slackDisconnect()));
				}
				return;
			}

			const manageAppsUrl = `${getATLContextUrl('id')}/manage-profile/apps`;
			window.open(manageAppsUrl);

			if (onCancelButtonClick) {
				onCancelButtonClick();
			}
		},
		[
			onCancelButtonClick,
			onThirdPartyIntegrationsChange,
			showFlag,
			slackDisconnect,
			slackWorkspaces.length,
		],
	);

	return (
		<ThirdPartyStateContext.Provider
			value={{
				isLoading: isLoading || isOrgSettingsLoading,
				isOk,
				slackWorkspaces,
				selectedSlackWorkspace,
				isSlackConnectDialogOpen,
				integrations: connectedIntegrations,
				enabledIntegrations,
				updateIntegrations: updateThirdPartyIntegrations,
				openSlackConnectDialog,
				closeSlackConnectDialog,
				onDisconnectSlackWorkspace,
				onThirdPartyIntegrationsChange,
				onChangeSelectedSlackWorkspace,
				queryThirdParty,
				getUserByThirdPartyId,
				cacheUserByThirdPartyId,
				onConnectProvider,
				onDisconnectProvider,
				abortThirdPartyRequests,
				orgSettings,
				isConnected,
				hideThirdPartyConnectLegend,
			}}
		>
			{children}
		</ThirdPartyStateContext.Provider>
	);
};

export const useThirdPartyState = () => {
	const state = useContext(ThirdPartyStateContext);

	if (state === undefined) {
		throw new Error('useThirdPartyState must be used within a ThirdPartyStateProvider');
	}

	return state;
};
